export class AxQuizz extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll(".ax-quizz__step"));
    this.currentStep = 0;

    this.overlay = this.querySelector(".ax-quizz__overlay");
    this.modal = this.querySelector(".ax-quizz__modal");
    this.btnNext = this.querySelector(".ax-quizz__btn-next");
    this.btnPrev = this.querySelector(".ax-quizz__btn-prev");
    this.btnReset = this.querySelector(".ax-quizz__btn-reset");
    this._resetBtn = this.querySelector(".ax-quizz__result-reset");

    this.formula = this.dataset.formula || "";
    this.roundResult = this.dataset.roundResult === "true";
    this.btnNextText = this.dataset.btnNextText || "Étape suivante";
    this.btnFinalText = this.dataset.btnFinalText || "Calculer";
    this.klaviyoEnabled = this.dataset.klaviyoEnabled === "true";
    this.klaviyoCompanyId = this.dataset.klaviyoCompanyId || "";
    this.klaviyoResultKey = this.dataset.klaviyoResultKey || "quizz_result";
    this.klaviyoListId = this.dataset.klaviyoListId || "";
    this.customerEmail = this.dataset.customerEmail || "";
    this.resultMode = this.dataset.resultMode || 'integrated';
    this.resultPage = this.dataset.resultPage || '';

    this._bindEvents();
    this._setupTrigger();
    this._updateView();
  }

  disconnectedCallback() {
    if (this._onKeydown) document.removeEventListener("keydown", this._onKeydown);
    if (this._onOpenRequest) document.removeEventListener("click", this._onOpenRequest);
    if (this._onExternalTrigger) document.removeEventListener("click", this._onExternalTrigger);
    this._inertBackground(false);
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
  }

  _bindEvents() {
    this.querySelector(".ax-quizz__close")?.addEventListener("click", () => this.close());
    this.overlay?.addEventListener("click", (e) => {
      if (e.target === this.overlay) this.close();
    });
    this.btnNext?.addEventListener("click", () => this._handleNext());
    this.btnPrev?.addEventListener("click", () => this._handlePrev());
    this.btnReset?.addEventListener("click", () => this._reset());
    this._resetBtn?.addEventListener("click", () => this._reset());

    this.querySelectorAll(".ax-quizz__input").forEach((input) => {
      // Sanitize the regex value: strip leading/trailing whitespace and non-breaking spaces
      // that Shopify can inject when escaping attribute values
      const rawRegex = input.dataset.regex;
      const regex = rawRegex
        ? rawRegex.replace(/[\u00A0\s]+/g, (m, offset) => (offset === 0 || offset === rawRegex.trimEnd().length - 1 ? "" : m)).trim()
        : null;

      if (rawRegex != null && regex !== rawRegex) input.dataset.regex = regex;

      if (regex) {
        // Build a "partial" regex: strip anchors, make mandatory quantifiers optional
        // so the user can type incrementally (e.g. "1" before "1.5" is valid in-progress).
        let partialPattern;
        try {
          partialPattern = new RegExp(
            "^(?:" +
              regex
                .replace(/^\^/, "")
                .replace(/\$$/, "")
                .replace(/(?<!\\)\+/g, "*")
                .replace(/(?<!\\)\{(\d+),?(\d*)?\}/g, "*") +
              ")$"
          );
        } catch {
          partialPattern = null;
        }

        input.addEventListener("beforeinput", (e) => {
          if (!e.data || !partialPattern) return;
          const start = input.selectionStart ?? input.value.length;
          const end = input.selectionEnd ?? input.value.length;
          const candidate = input.value.slice(0, start) + e.data + input.value.slice(end);
          if (!partialPattern.test(candidate)) {
            e.preventDefault();
          }
        });
      }

      input.addEventListener("input", () => {
        if (input.classList.contains("is-invalid")) {
          this._validateField(input);
        }
      });
    });
  }

  _setupTrigger() {
    // Déclencheur externe découplé : n'importe quel [data-ax-quizz-open] ouvre ce quizz.
    // Valeur vide = ce quizz ; sinon on ne réagit que si elle correspond à this.id.
    this._onOpenRequest = (e) => {
      const trigger = e.target instanceof Element ? e.target.closest("[data-ax-quizz-open]") : null;
      if (!trigger) return;
      const target = trigger.getAttribute("data-ax-quizz-open");
      if (!target || target === this.id) this.open();
    };
    document.addEventListener("click", this._onOpenRequest);

    const triggerType = this.dataset.triggerType;

    if (triggerType === "button") {
      this.querySelector(".ax-quizz__trigger-btn")?.addEventListener("click", () => this.open());
    } else {
      const triggerId = this.dataset.triggerId;
      if (!triggerId) return;

      // Chaque entrée du réglage est un id nu (`mon-bouton`) ou un sélecteur CSS complet
      // (`a.button--xxx`) — tous les boutons du thème n'ont pas d'id.
      const selector = triggerId
        .split(",")
        .map((part) => part.trim())
        .filter(Boolean)
        .map((part) => (/[.#[\]>+~:\s]/.test(part) ? part : `#${part}`))
        .join(", ");
      if (!selector) return;

      // Délégation : le sélecteur étant évalué au clic, un déclencheur rendu après coup
      // (éditeur, section fetchée) reste fonctionnel.
      this._onExternalTrigger = (e) => {
        if (!(e.target instanceof Element)) return;
        let trigger;
        try {
          trigger = e.target.closest(selector);
        } catch {
          return;
        }
        if (!trigger) return;
        if (trigger.tagName === "A" || e.target.closest("a")) e.preventDefault();
        this.open();
      };
      document.addEventListener("click", this._onExternalTrigger);
    }
  }

  open() {
    if (this.overlay?.classList.contains("is-open")) return;
    this._returnFocusEl = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    this.overlay?.classList.add("is-open");
    this._scrollY = window.scrollY;
    document.body.style.overflow = "hidden";
    document.body.style.position = "fixed";
    document.body.style.top = `-${this._scrollY}px`;
    document.body.style.width = "100%";

    this._inertBackground(true);

    if (!this._onKeydown) this._onKeydown = (e) => this._handleKeydown(e);
    document.addEventListener("keydown", this._onKeydown);

    // Focus initial sur le bouton de fermeture (pattern WAI-ARIA dialog).
    const focusTarget = this.querySelector(".ax-quizz__close") ?? this._getFocusable()[0];
    focusTarget?.focus({ preventScroll: true });
  }

  close() {
    if (!this.overlay?.classList.contains("is-open")) return;
    this.overlay?.classList.remove("is-open");
    document.body.style.overflow = "";
    document.body.style.position = "";
    document.body.style.top = "";
    document.body.style.width = "";
    window.scrollTo({ top: this._scrollY || 0, left: 0, behavior: "instant" });

    this._inertBackground(false);
    if (this._onKeydown) document.removeEventListener("keydown", this._onKeydown);

    this._returnFocusEl?.focus({ preventScroll: true });
    this._returnFocusEl = null;
  }

  _handleKeydown(e) {
    if (e.key === "Escape") {
      this.close();
      return;
    }
    if (e.key !== "Tab") return;

    const focusables = this._getFocusable();
    if (!focusables.length) return;

    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement;

    if (e.shiftKey && (active === first || !this.modal?.contains(active))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  // Éléments focusables VISIBLES de la modale (exclut les étapes/résultats masqués).
  _getFocusable() {
    if (!this.modal) return [];
    const selector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    return Array.from(this.modal.querySelectorAll(selector)).filter(
      (el) => !el.hasAttribute("hidden") && el.offsetParent !== null
    );
  }

  // Rend inertes (non focusables + hors arbre d'accessibilité) tous les éléments
  // hors modale, en remontant de l'overlay au body niveau par niveau.
  _inertBackground(enable) {
    if (!this.overlay) return;

    if (!enable) {
      for (const el of this._inertedEls ?? []) el.removeAttribute("inert");
      this._inertedEls = [];
      return;
    }

    this._inertedEls = [];
    let node = this.overlay;
    while (node && node !== document.body) {
      const parent = node.parentElement;
      if (!parent) break;
      for (const sibling of parent.children) {
        if (sibling !== node && !sibling.hasAttribute("inert")) {
          sibling.setAttribute("inert", "");
          this._inertedEls.push(sibling);
        }
      }
      node = parent;
    }
  }

  _handleNext() {
    if (!this._validateStep(this.currentStep)) return;

    if (this.currentStep < this.steps.length - 1) {
      this.currentStep++;
      this._updateView();
    } else {
      this._calculate();
    }
  }

  _handlePrev() {
    if (this.currentStep > 0) {
      this.currentStep--;
      this._updateView();
    }
  }

  _validateField(field) {
    if (field.required && !field.value.trim()) {
      field.classList.add("is-invalid");
      return false;
    }

    if (field.value && field.validity && !field.validity.valid) {
      field.classList.add("is-invalid");
      return false;
    }

    const regex = field.dataset.regex;
    if (regex && field.value) {
      try {
        const re = new RegExp(regex);
        const valid = re.test(field.value);
        field.classList.toggle("is-invalid", !valid);
        return valid;
      } catch {
        // invalid regex — skip
      }
    }

    field.classList.remove("is-invalid");
    return true;
  }

  _validateStep(stepIndex) {
    const step = this.steps[stepIndex];
    let valid = true;
    step.querySelectorAll(".ax-quizz__input").forEach((input) => {
      if (!this._validateField(input)) valid = false;
    });
    return valid;
  }

  _updateView() {
    this.steps.forEach((step, i) => {
      step.classList.toggle("is-active", i === this.currentStep);
    });

    const isLast = this.currentStep === this.steps.length - 1;
    const stepLabel = isLast ? "" : ` (${this.currentStep + 1}/${this.steps.length})`;

    if (this.btnNext) {
      this.btnNext.textContent = `${isLast ? this.btnFinalText : this.btnNextText}${stepLabel}`;
    }
    if (this.btnPrev) {
      this.btnPrev.style.display = this.currentStep === 0 ? "none" : "";
    }
    if (this.modal) this.modal.scrollTop = 0;
  }

  // calc: always the raw value (used for formula).
  // param: label instead of value when data-use-label is set.
  _getValues() {
    const calc = {};
    const param = {};
    this.querySelectorAll("[data-question-id]").forEach((el) => {
      const id = el.dataset.questionId;
      if (!id) return; // question sans identifiant → ignorée (sinon calc[""] casse la formule)
      calc[id] = el.value;
      if (el.dataset.useLabel === "true") {
        const selected = el.options?.[el.selectedIndex];
        param[id] = selected?.dataset.label ?? el.value;
      } else {
        param[id] = el.value;
      }
    });
    return { calc, param };
  }

  _calculate() {
    if (!this._validateStep(this.currentStep)) return;

    const { calc: calcValues, param: paramValues } = this._getValues();
    const keys = Object.keys(calcValues).sort((a, b) => b.length - a.length);
    let result = 0;

    if (this.formula) {
      let formula = this.formula
        .replace(/\u00A0/g, " ")
        .replace(/×/g, "*")
        .replace(/\u2212/g, "-")
        .replace(/,/g, ".");

      keys.forEach((key) => {
        if (!key) return; // garde-fou : replaceAll("") ins\u00e9rerait la valeur entre chaque caract\u00e8re
        const normalized = calcValues[key].replace(/\u2212/g, "-").replace(/,/g, ".");
        const val = Number.isFinite(parseFloat(normalized)) ? parseFloat(normalized) : 0;
        formula = formula.replaceAll(key, `(${val})`);
      });

      formula = formula.replace(/\)\s*\(/g, ")+(");

      if (/^[\d\s+\-*/().]+$/.test(formula)) {
        try {
          result = new Function(`return (${formula})`)();
          if (!Number.isFinite(result)) result = 0;
        } catch (e) {
          console.error("AxQuizz: Formula calculation failed", e);
          result = 0;
        }
      } else {
        console.warn("AxQuizz: Invalid formula after replacement", formula);
      }
    }

    if (this.roundResult) result = Math.round(result);

    const done = () => {
      if (this.resultMode === 'external') {
        this._redirectToResultPage(result, paramValues);
      } else {
        this._showResult(result, paramValues);
      }
    };

    if (this.klaviyoEnabled) {
      this._sendToKlaviyo(result, paramValues, done);
    } else {
      done();
    }
  }

  _klaviyoProperties() {
    return {};
  }

  _sendToKlaviyo(result, paramValues, onDone) {
    const emailInput = this.querySelector('[data-question-id="email"]');
    const email = emailInput?.value.trim() || this.customerEmail;

    if (!email || !this.klaviyoCompanyId) {
      onDone();
      return;
    }

    const properties = {};
    if (result !== null) {
      properties[this.klaviyoResultKey] = result;
    }
    this.querySelectorAll("[data-question-id][data-klaviyo='true']").forEach((el) => {
      const id = el.dataset.questionId;
      properties[id] = paramValues[id] ?? el.value;
    });
    Object.assign(properties, this._klaviyoProperties());

    const consentCheckbox = this.querySelector(".ax-quizz__consent-checkbox");
    const consentChecked = !!consentCheckbox?.checked;
    if (consentChecked) {
      properties["Accepts Marketing"] = true;
    }

    const headers = {
      accept: "application/vnd.api+json",
      revision: "2025-01-15",
      "content-type": "application/vnd.api+json",
    };
    const companyIdParam = `company_id=${encodeURIComponent(this.klaviyoCompanyId)}`;

    const requests = [
      fetch(`https://a.klaviyo.com/client/profiles/?${companyIdParam}`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          data: {
            type: "profile",
            attributes: { email, properties },
          },
        }),
      }),
    ];

    if (consentChecked && this.klaviyoListId) {
      requests.push(
        fetch(`https://a.klaviyo.com/client/subscriptions/?${companyIdParam}`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: "Ax Quizz",
                profile: {
                  data: {
                    type: "profile",
                    attributes: {
                      email,
                      subscriptions: {
                        email: { marketing: { consent: "SUBSCRIBED" } },
                      },
                    },
                  },
                },
              },
              relationships: {
                list: { data: { type: "list", id: this.klaviyoListId } },
              },
            },
          }),
        })
      );
    }

    Promise.allSettled(requests).finally(() => {
      onDone();
    });
  }

  _showResult(total, values) {
    const numericTotal = Number.isFinite(total) ? total : 0;

    for (const step of this.steps) step.classList.remove("is-active");
    const actions = this.querySelector(".ax-quizz__actions");
    if (actions) actions.hidden = true;

    const results = Array.from(this.querySelectorAll(".ax-quizz__result"));
    const match = this._matchRange(results, numericTotal);
    for (const r of results) r.hidden = r !== match;

    this.classList.add("is-result");
    if (this._resetBtn) this._resetBtn.hidden = false;
    if (this.modal) this.modal.scrollTop = 0;

    if (!match) return;
    this._fillPlaceholders(match, numericTotal, values);
    this._animateTotals(match, numericTotal);
  }

  _matchRange(results, total) {
    if (!results.length) return null;

    for (const r of results) {
      const min = parseFloat(r.dataset.rangeMin);
      const max = parseFloat(r.dataset.rangeMax);
      if (Number.isFinite(min) && Number.isFinite(max) && total >= min && total < max) return r;
    }

    let lowest = results[0];
    let highest = results[0];
    for (const r of results) {
      if (parseFloat(r.dataset.rangeMin) < parseFloat(lowest.dataset.rangeMin)) lowest = r;
      if (parseFloat(r.dataset.rangeMax) > parseFloat(highest.dataset.rangeMax)) highest = r;
    }
    return total < parseFloat(lowest.dataset.rangeMin) ? lowest : highest;
  }

  _replaceKeys(text, values) {
    return text.replace(/\[([a-zA-Z0-9_-]+)\]/g, (m, key) =>
      Object.prototype.hasOwnProperty.call(values, key) ? values[key] : ""
    );
  }

  _formatNumber(n) {
    return Math.round(n)
      .toString()
      .replace(/\B(?=(\d{3})+(?!\d))/g, " ");
  }

  _fillPlaceholders(root, total, values) {
    const formatted = this._formatNumber(total);
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);

    for (const node of nodes) {
      const text = node.nodeValue;
      if (!text || text.indexOf("[") === -1) continue;

      if (text.indexOf("[total]") !== -1) {
        const frag = document.createDocumentFragment();
        const parts = text.split("[total]");
        parts.forEach((part, i) => {
          frag.appendChild(document.createTextNode(this._replaceKeys(part, values)));
          if (i < parts.length - 1) {
            const span = document.createElement("span");
            span.className = "ax-quizz__total";
            span.dataset.resultValue = String(total);
            span.textContent = formatted;
            frag.appendChild(span);
          }
        });
        node.parentNode.replaceChild(frag, node);
      } else {
        const replaced = this._replaceKeys(text, values);
        if (replaced !== text) node.nodeValue = replaced;
      }
    }
  }

  _animateTotals(root, total) {
    const els = root.querySelectorAll("[data-result-value]");
    if (!els.length) return;

    const target = Number.isFinite(total) ? total : 0;
    const duration = 2500;
    const start = performance.now();

    const step = (now) => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      const current = this._formatNumber(Math.floor(eased * target));
      for (const el of els) el.textContent = current;
      if (p < 1) {
        requestAnimationFrame(step);
      } else {
        const final = this._formatNumber(target);
        for (const el of els) el.textContent = final;
      }
    };

    requestAnimationFrame(step);
  }

  _redirectToResultPage(result, paramValues) {
    if (!this.resultPage) {
      console.warn('AxQuizz: No result page configured for external mode.');
      return;
    }

    // Le réglage `url` du thème sort un chemin non localisé : on repasse par
    // `Shopify.routes.root` pour ne pas perdre le préfixe de locale.
    const root = window.Shopify?.routes?.root ?? '/';
    let target = this.resultPage;
    if (target.startsWith('/') && !target.startsWith(root)) {
      target = root + target.slice(1);
    }

    const url = new URL(target, window.location.origin);
    for (const [key, value] of Object.entries(paramValues)) {
      url.searchParams.set(key, value);
    }

    if (Number.isFinite(result)) {
      url.searchParams.set('result', String(result));
    }

    window.location.href = url.toString();
  }

  _reset() {
    this.currentStep = 0;
    this.classList.remove("is-result");
    const actions = this.querySelector(".ax-quizz__actions");
    if (actions) actions.hidden = false;
    if (this._resetBtn) this._resetBtn.hidden = true;
    for (const r of this.querySelectorAll(".ax-quizz__result")) r.hidden = true;

    this.querySelectorAll(".ax-quizz__input").forEach((el) => {
      el.value = "";
      el.classList.remove("is-invalid");
    });
    this.querySelectorAll(".ax-quizz__select").forEach((el) => {
      el.selectedIndex = 0;
    });
    this.querySelectorAll(".ax-quizz__consent-checkbox").forEach((el) => {
      el.checked = false;
    });
    this._updateView();
  }
}

customElements.get("ax-quizz") || customElements.define("ax-quizz", AxQuizz);
